import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MdDashboard,
  MdFactory,
  MdLocalShipping,
  MdOutlineInventory,
  MdHistoryEdu,
  MdWorkHistory,
  MdSettings,
  MdExitToApp,
  MdMenu,
  MdSwapHoriz,
} from "react-icons/md";
import { GiGraveFlowers } from "react-icons/gi";
import { TbTruckDelivery } from "react-icons/tb";
import { HiUsers } from "react-icons/hi";
import { CgOrganisation } from "react-icons/cg";

import profile from "../images/profile.jpg";
import "../dashboard-redesign.css";
import "../dark-chrome.css";
import { can, isGroupOwner, roleLabel } from "../auth/permissions";
import { useAuth } from "../auth/AuthContext";
import { clearActiveBusinessId } from "../auth/tokenStore";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { brandFor } from "../businessBranding";
import { useBranding } from "../theme/BrandingProvider";
import NotificationBell from "./NotificationBell";
import BusinessSwitcher from "./BusinessSwitcher";
import { useIncomingTransferCount } from "../notifications/useIncomingTransferCount";

/* ------------------------------------------------------------------ *
 * App shell for the redesign: persistent sidebar + top bar.
 *
 * Replaces the hamburger drawer (Header / SideNavigationSlider in
 * components.js). Rendered by the page itself rather than BasePage, so it
 * can be adopted one screen at a time — `/dashboard-v2` sits in BasePage's
 * `avoidHeader` list and this shell owns the chrome for that route.
 * ------------------------------------------------------------------ */

/* Paths checked against the routing table in App.js. `countKey` picks a live
   badge value out of the `counts` prop when the page supplies one. Icons
   match the legacy drawer (SideNavigationSlider) for continuity.

   `perm` is the server permission the destination needs — an item without one
   is reachable by every role. Hiding what a role can't use beats letting them
   click into a 403; the server still checks every request either way.

   `hidden` keeps an item out of the sidebar while its route still works and
   still titles the top bar. */
const NAV_PRIMARY = [
  { label: "Dashboard", to: "/dashboard-v2", icon: MdDashboard, match: ["/", "/dashboard-v1"], perm: "workorders:read" },
  { label: "Work orders", to: "/work-orders", countKey: "workOrders", icon: MdFactory, match: ["/work-orders", "/WorkOrderList", "/work-order/"], perm: "workorders:read" },
  { label: "Ordered products", to: "/ordered-products", countKey: "orderedProducts", icon: MdLocalShipping, match: ["/ordered-products-v1"], perm: "workorders:read" },
  { label: "Delivery runs", to: "/delivery-runs", icon: TbTruckDelivery, match: ["/DeliveryRunList", "/RunSheetMaking", "/EditRunSheet", "/editRunSheet/", "/run-sheet/"], perm: "runsheets:read" },
  /* Orders on their way in from (or out to) another business. The count is
     the pending INCOMING ones — the requests that need an answer. */
  { label: "Transfers", to: "/transfers", countKey: "transfers", icon: MdSwapHoriz, match: ["/transfers"], perm: "workorders:transfer" },
];

const NAV_SECONDARY = [
  { label: "Products", to: "/products", icon: GiGraveFlowers, match: ["/products/", "/products-v1", "/newProducts"], perm: "products:read" },
  { label: "Inventory", to: "/inventory", icon: MdOutlineInventory, match: ["/InventoryList"], perm: "inventory:read", hidden: true },
  { label: "Funeral directors", to: "/funeral-directors", icon: CgOrganisation, match: ["/FuneralDirectors"], perm: "fd:read" },
  { label: "Customers history", to: "/customer-history", icon: MdHistoryEdu, match: ["/customer-history-v1"], perm: "workorders:read", hidden: true },
  { label: "Work history", to: "/work-history", icon: MdWorkHistory, match: ["/work-history-v1"], perm: "workorders:read", hidden: true },
  { label: "Staff", to: "/staff", icon: HiUsers, perm: "users:manage" },
  { label: "Settings", to: "/settings", icon: MdSettings, match: ["/settings-v1", "/settings-v2"], perm: "settings:read" },
];

/* Page title for the top bar, derived from the nav tables. */
const TITLE_EXTRAS = {
  "/": "Dashboard",
  "/dashboard-v1": "Dashboard (legacy)",
  "/dashboard-v2": "Today",
  "/settings-v1": "Settings (legacy)",
  "/InventoryList-v1": "Inventory (legacy)",
  "/work-history-v1": "Work history (legacy)",
  "/customer-history-v1": "Customers history (legacy)",
  "/ChooseOrderType": "New Order",
  "/new-order": "New work order",
  "/work-orders": "Work orders",
  "/run-sheet/new": "Run sheet",
  "/ordered-products-v1": "Ordered products (legacy)",
  "/products/new": "New product",
  "/products-v1": "Products (legacy)",
  "/newProducts": "Add product (legacy)",
  "/FuneralDirectors-v1": "Funeral directors (legacy)",
  "/DeliveryRunList-v1": "Delivery runs (legacy)",
  "/FuneralOrderInput": "Funeral Order",
  "/RetailOrderInput": "Retail Order",
  "/backup": "Backup",
  "/production-floor": "Production Floor",
  "/notifications": "Notifications",
  "/transfers": "Transfers",
  "/transfers/outgoing": "Sent transfers",
};
/* Param-carrying paths that no nav item owns. */
const TITLE_PREFIXES = [["/transfers/", "Transfer request"]];
export function titleForPath(pathname) {
  if (TITLE_EXTRAS[pathname]) return TITLE_EXTRAS[pathname];
  const prefixed = TITLE_PREFIXES.find(([p]) => pathname.indexOf(p) === 0);
  if (prefixed) return prefixed[1];
  const items = [...NAV_PRIMARY, ...NAV_SECONDARY];
  const hit =
    items.find((i) => i.to === pathname) ||
    items.find(
      (i) =>
        pathname.indexOf(i.to + "/") === 0 ||
        (i.match || []).some((m) => pathname.indexOf(m) === 0)
    );
  return hit ? hit.label : "Ray's Florist";
}

function NavItem({ item, count, secondary, active, onClick }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={[
        "rf-nav-item",
        secondary ? "is-secondary" : "",
        active ? "is-active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={item.label}
      onClick={onClick}
    >
      <span className="rf-nav-dot" />
      <span className="rf-nav-label">{item.label}</span>
      {count !== undefined && count !== null && (
        <span className="rf-nav-count">{count}</span>
      )}
      {Icon && <Icon className="rf-nav-icon" />}
    </button>
  );
}

/* Collapsed = icon rail. Manual choice persists; below 1200px the rail is
   forced (matching the old CSS-only breakpoint). */
function useCollapsed() {
  const stored = () => localStorage.getItem("rf-nav-collapsed") === "1";
  const forced = () => window.innerWidth <= 1200;
  const [collapsed, setCollapsed] = useState(() => forced() || stored());
  useEffect(() => {
    const onResize = () => setCollapsed(forced() || stored());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("rf-nav-collapsed", next ? "1" : "0");
      return next;
    });
  };
  return [collapsed, toggle];
}

/* ---- sidebar slide ------------------------------------------------ *
   The sidebar's edge slides straight; the rows inside it slide at an
   angle — the top row leads and lands first, each row below a touch
   behind, the bottom row last. Collapsing runs the same stagger inward
   (top first) and only then flips to the rail layout.

   The container's speed is set by the rows it must not clip: expanding
   it keeps pace with the fastest (top) row, so no row overtakes the
   edge; collapsing it runs on the slowest (bottom) row's exact timing,
   so no icon is cut off before its row has slid in. */
const RAIL_W = 72; // .rf-app.is-collapsed .rf-sidebar in dashboard-redesign.css
const TOP_MS = 420; // the top row's slide
const BOTTOM_MS = 720; // the bottom row's slide
/* One curve for everything: moving from the first frame, gentle landing.
   (A slow-start curve made the lower half sit still for ~250ms and then
   race — the "stuck, then snap".) Lower rows lag by being slower, never
   by waiting. */
const EASE = "cubic-bezier(0.33, 1, 0.68, 1)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const ROWS = ".rf-nav > *, .rf-sidebar-exit, .rf-sidebar-version";
/* the text in a row, faded out as the row slides in on collapse */
const ROW_TEXT = ".rf-nav-label, .rf-nav-dot, .rf-nav-count, .rf-exit-label";
const ICONS = ".rf-nav-icon";

function iconRects(el) {
  const map = new Map();
  el.querySelectorAll(ICONS).forEach((node) => map.set(node, node.getBoundingClientRect()));
  return map;
}

/* Glide each icon from where it was before a layout flip to where it is
   now, instead of letting it jump (the rail centres icons, the full
   layout right-aligns them). `timing(node)` gives each its duration/delay. */
function flipIcons(before, timing, anims) {
  before.forEach((r0, node) => {
    if (!node.isConnected) return;
    const r1 = node.getBoundingClientRect();
    const dx = r0.left - r1.left;
    const dy = r0.top - r1.top;
    const k = r0.width / (r1.width || 1);
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(k - 1) < 0.01) return;
    anims.push(
      node.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${k})`, transformOrigin: "0 0" },
          { transform: "none", transformOrigin: "0 0" },
        ],
        { fill: "backwards", ...timing(node) }
      )
    );
  });
}

/* Rows move as whole units, each a touch behind the row above it, so
   together they form the angle. Landing times spread evenly from the top
   row (TOP_MS) to the bottom row (BOTTOM_MS), reached mostly by moving
   slower, with only a short head start for the rows above, so no row ever
   sits still waiting its turn. */
function rowTiming(rows) {
  const lag = BOTTOM_MS - TOP_MS;
  const last = Math.max(1, rows.length - 1);
  return (i) => {
    const y = i / last;
    return { delay: y * lag * 0.25, duration: TOP_MS + y * lag * 0.75 };
  };
}

function slideSidebar(el, from, to, expanding) {
  const anims = [];
  const rows = Array.from(el.querySelectorAll(ROWS));
  const timing = rowTiming(rows);
  const travel = Math.abs(to - from);
  /* Rows are 100% of a sidebar whose width is mid-animation; pin them at
     the open width so they glide as rigid rows (the sidebar clips them)
     rather than stretching with it. */
  const full = Math.max(from, to);
  const pinned = Array.from(el.children);
  pinned.forEach((child) => {
    child.style.minWidth = `${full}px`;
  });
  /* The page beside the sidebar is the expensive part: animating the
     sidebar's width resizes it every frame, and every table, centred
     layout and responsive chart in it re-lays out (and redraws) each
     frame — on a heavy page the main thread falls behind and the slide
     stalls, then jumps to the end. Hold the page content at the width it
     will END at, so frames only shift it sideways; the main column clips
     the overhang (overflow: clip keeps sticky headers working). */
  const main = el.nextElementSibling;
  const content = main && main.firstElementChild;
  if (content) {
    const now = content.getBoundingClientRect().width;
    // expanding: the layout already flipped, so `now` is the final width
    const final = expanding ? now : now + travel;
    content.style.flex = "0 0 auto";
    content.style.width = `${final}px`;
    main.style.overflowX = "clip";
  }
  const unpin = () => {
    pinned.forEach((child) => {
      child.style.minWidth = "";
    });
    if (content) {
      content.style.flex = "";
      content.style.width = "";
      main.style.overflowX = "";
    }
  };
  if (expanding) {
    // straight edge, as fast as the top row
    anims.push(
      el.animate([{ width: `${from}px` }, { width: `${to}px` }], { duration: TOP_MS, easing: EASE })
    );
    // each row starts pushed back by the distance the edge travels (its
    // icon about where the rail had it) and slides out behind the edge
    rows.forEach((row, i) => {
      anims.push(
        row.animate(
          [
            { transform: `translateX(-${travel}px)`, opacity: 0.35 },
            { transform: "none", opacity: 1 },
          ],
          { ...timing(i), easing: EASE, fill: "backwards" }
        )
      );
    });
  } else {
    // straight edge on exactly the bottom row's timing (delay included):
    // every row above is ahead of it, so the edge never cuts an icon
    anims.push(
      el.animate([{ width: `${from}px` }, { width: `${to}px` }], {
        ...timing(rows.length - 1),
        easing: EASE,
        fill: "both",
      })
    );
    // rows slide back in, top first, their text fading as it goes
    rows.forEach((row, i) => {
      anims.push(
        row.animate([{ transform: "none" }, { transform: `translateX(-${travel}px)` }], {
          ...timing(i),
          easing: EASE,
          fill: "forwards",
        })
      );
      const parts = row.matches(".rf-nav-eyebrow") ? [row] : row.querySelectorAll(ROW_TEXT);
      parts.forEach((part) =>
        anims.push(
          part.animate([{ opacity: 1 }, { opacity: 0 }], {
            ...timing(i),
            easing: EASE,
            fill: "forwards",
          })
        )
      );
    });
  }
  /* Normally every animation lands and `finished` resolves. A paused or
     throttled one (tab hidden mid-slide) never reports finished, which
     would leave the page pinned — so after a grace period force the end
     state instead of waiting. */
  const landed = Promise.all(anims.map((a) => a.finished)).catch(() => {});
  const failsafe = new Promise((resolve) => setTimeout(resolve, BOTTOM_MS + 600));
  return {
    finished: Promise.race([landed, failsafe]).then(() =>
      anims.forEach((a) => {
        try {
          a.finish();
        } catch (e) {
          /* cancelled already */
        }
      })
    ),
    cancel: () => {
      anims.forEach((a) => a.cancel());
      unpin();
    },
  };
}

/* Wraps the collapse toggle with the slide. Returns the new toggle. */
function useSidebarSlide(sidebarRef, collapsed, toggle) {
  const running = useRef(null);
  const pendingExpand = useRef(false);

  // expanding: flip the layout first, then slide from the rail width
  useLayoutEffect(() => {
    if (!pendingExpand.current || collapsed) return;
    pendingExpand.current = false;
    const el = sidebarRef.current;
    if (!el) return;
    const slide = slideSidebar(el, RAIL_W, el.getBoundingClientRect().width, true);
    running.current = slide;
    slide.finished.then(() => {
      slide.cancel(); // all landed; this just unpins the rows
      if (running.current === slide) running.current = null;
    });
  }, [collapsed, sidebarRef]);

  useEffect(() => () => running.current && running.current.cancel(), []);

  return () => {
    const el = sidebarRef.current;
    if (!el || !el.animate || prefersReducedMotion()) return toggle();
    // the icons' final settle is cosmetic — a new toggle cuts it short
    if (running.current && running.current.soft) {
      running.current.cancel();
      running.current = null;
    }
    if (running.current) return; // let the current slide land
    if (collapsed) {
      pendingExpand.current = true;
      toggle();
      return;
    }
    // collapsing: slide the expanded layout in, then flip to the rail
    const slide = slideSidebar(el, el.getBoundingClientRect().width, RAIL_W, false);
    running.current = slide;
    slide.finished.then(() => {
      const before = iconRects(el);
      flushSync(toggle); // commit the rail layout before dropping the held frame
      slide.cancel();
      // settle the icons into the rail's centred column; the group labels,
      // now thin rules, fade back in
      const settle = [];
      flipIcons(before, () => ({ duration: 320, easing: EASE }), settle);
      el.querySelectorAll(".rf-nav-eyebrow").forEach((rule) =>
        settle.push(rule.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: EASE }))
      );
      const settling = { soft: true, cancel: () => settle.forEach((a) => a.cancel()) };
      running.current = settling;
      Promise.all(settle.map((a) => a.finished))
        .catch(() => {})
        .then(() => {
          if (running.current === settling) running.current = null;
        });
    });
  };
}

/* A 403 means the signed-in user's role can't do this — never a logout.
   axiosConfig dispatches the event; the shell is the one place mounted on
   every screen, so the message lands here. */
function useForbiddenToast() {
  useEffect(() => {
    const onForbidden = (e) => {
      const raw = (e.detail || {}).message || "";
      const missing = raw.split("Missing permission:")[1];
      toast.warn(
        missing
          ? `You don't have permission for this (${missing.trim()}).`
          : "You don't have permission for this.",
        { toastId: raw || "rf-forbidden" }
      );
    };
    window.addEventListener("rf:forbidden", onForbidden);
    return () => window.removeEventListener("rf:forbidden", onForbidden);
  }, []);
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
}

/* `bare` renders no chrome but keeps the exact same wrapper structure —
   the wrappers become display:contents — so toggling chrome mid-session
   (hideHeader, the run-sheet overlay) never remounts the routed page and
   never wipes its state. */
/* `slim` is for popped-out work windows (the run sheet builder): no
   sidebar, and the header shrinks to a thin strip with just a small logo. */
export function AppShell({ title, user, counts = {}, onLogout, bare = false, slim = false, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const clock = useClock();
  const [collapsed, toggleCollapsedNow] = useCollapsed();
  const sidebarRef = useRef(null);
  const toggleCollapsed = useSidebarSlide(sidebarRef, collapsed, toggleCollapsedNow);
  useForbiddenToast();

  /* The page name no longer shows in the header (the brand sits there);
     it still names the browser tab. */
  useEffect(() => {
    if (title) document.title = `${title} \u2014 Ray's Florist`;
  }, [title]);

  /* The header follows the business that is actually open (the one stored
     when it was picked on the overview), not the token's claim — those can
     differ until the server honours the business-id header. */
  const activeBusiness = useActiveBusiness();
  // Non-owners never see the group overview, so they have nowhere to exit to.
  const { memberships } = useAuth() || {};
  const canExit = isGroupOwner(user, memberships);
  const businessName =
    (activeBusiness && (activeBusiness.name || activeBusiness.code)) || "Business";
  useBranding(activeBusiness); // re-render when Settings saves a new logo
  const brand = brandFor(activeBusiness);
  /* A match ending in "/" is a prefix (e.g. "/work-order/" covers every
     order). The root "/" is the exception — every path starts with it, so
     as a prefix it lit Dashboard on every page; it only matches exactly. */
  const isActive = (item) =>
    location.pathname === item.to ||
    (item.match || []).some(
      (m) =>
        location.pathname === m ||
        (m !== "/" && m.endsWith("/") && location.pathname.indexOf(m) === 0)
    );

  // Role-based nav: drop destinations the signed-in role has no permission
  // for (a driver, for example, never sees Settings).
  const allowed = (item) => !item.hidden && (!item.perm || can(user, item.perm));
  const primary = NAV_PRIMARY.filter(allowed);
  const secondary = NAV_SECONDARY.filter(allowed);

  /* Transfers waiting on us. The page supplies the other counts; this one
     is the shell's own, so the badge is right on every screen. */
  const transfers = useIncomingTransferCount(can(user, "workorders:transfer"));
  const navCounts = transfers ? { ...counts, transfers } : counts;

  return (
    <div className={bare ? "rf-bare" : `rf-app${collapsed ? " is-collapsed" : ""}`}>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* The header sits ABOVE the sidebar and spans the full width;
          sidebar + main share the .rf-app-row below it. The user chip
          lives in the header, and so does the business identity (logo +
          name) with the hamburger that collapses the sidebar rail. The
          page name used to sit here; the active nav item carries it now. */}
      {!bare && slim && (
      <header className="rf-topbar is-slim">
        {brand.logo ? (
          <img src={brand.logo} alt={businessName} />
        ) : (
          <span className="rf-brand-mark" aria-hidden="true">{brand.monogram}</span>
        )}
      </header>
      )}
      {!bare && !slim && (
      <header className="rf-topbar">
        <button
          type="button"
          className="rf-hamburger"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand menu" : "Collapse menu"}
        >
          <MdMenu />
        </button>
        <div className="rf-topbar-brand">
          {/* white card hanging below the bar, so the logo never sits on the brand colour */}
          <span className="rf-topbar-logo">
            {brand.logo ? (
              <img src={brand.logo} alt="" />
            ) : (
              /* No artwork for this business: a monogram stands in. */
              <span className="rf-brand-mark" aria-hidden="true">{brand.monogram}</span>
            )}
          </span>
          <span className="rf-serif">{businessName}</span>
        </div>

        <span className="rf-spacer" />

        <div className="rf-clock">
          <b>{clock.date}</b>
          <span>{clock.time}</span>
        </div>

        <NotificationBell />

        <div className="rf-topbar-user">
          <img src={profile} alt="" />
          <div className="rf-tb-user-text">
            <div className="rf-tb-user-name">
              {(user || {}).full_name || (user || {}).userName || "\u2014"}
            </div>
            <div className="rf-tb-user-role">{roleLabel(user)}</div>
          </div>
          {onLogout && (
            <button type="button" className="rf-tb-logout" onClick={onLogout}>
              Log out
            </button>
          )}
        </div>
      </header>
      )}

      <div className={bare ? "rf-bare" : "rf-app-row"}>
      {!bare && !slim && (
      <aside className="rf-sidebar" ref={sidebarRef}>
        <nav className="rf-nav">
          <div className="rf-nav-eyebrow">Today</div>
          {primary.map((item) => (
            <NavItem
              key={item.label}
              item={item}
              count={item.countKey ? navCounts[item.countKey] : undefined}
              active={isActive(item)}
              onClick={() => navigate(item.to)}
            />
          ))}

          <div className="rf-nav-eyebrow is-second">Records</div>
          {secondary.map((item) => (
            <NavItem
              key={item.label}
              item={item}
              secondary
              active={isActive(item)}
              onClick={() => navigate(item.to)}
            />
          ))}
        </nav>

        {/* only renders for someone in more than one business */}
        <BusinessSwitcher />

        {canExit && (
        <div className="rf-sidebar-exit">
          <button
            type="button"
            className="rf-exit-btn"
            title={`Exit ${businessName} — back to the group overview`}
            onClick={() => {
              // Drop the active business before leaving, so nothing this page
              // still has in flight goes out with a `business-id` header.
              clearActiveBusinessId();
              navigate("/overview");
            }}
          >
            <span className="rf-exit-label">
              Exit {businessName}
            </span>
            <MdExitToApp className="rf-nav-icon" />
          </button>
        </div>
        )}

        <div className="rf-sidebar-version">Ray&apos;s Florist</div>
      </aside>
      )}

      <div className={bare ? "rf-bare" : "rf-main"}>
        {/* Same centering context legacy pages had under .app-content
            (row flex, justify-content: center) before the shell rollout. */}
        <div className={bare ? "rf-bare" : "rf-content"}>{children}</div>
      </div>
      </div>
    </div>
  );
}

export default AppShell;
